"use client"

import React from "react"
import { toast } from "react-hot-toast"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Calendar, Clock, User, Building, Edit, UserCheck, UserX } from "lucide-react"
import type { Application, ScheduleFormData, ValidationErrors, UserType } from "../components/types/application.types"
import type { InterviewDetailsResponse } from "../services/interviewapi"
import {
  getLastRound,
  canScheduleRound,
  canRescheduleRound,
  canSubmitResult,
  formatTimeTo12Hour,
  getNextRoundName,
  isFinalRound,
  getInterviewOwnershipConflict,
} from "../components/utils/application.utils"
import { ScheduleForm } from "../components/ScheduleForm"
import { InterviewOwnershipModal } from "../components/InterviewOwnershipModal"
import { rescheduleInterviewRound, getInterviewDetailsByApplicationId } from "../services/interviewapi" // Adjusted import

interface InterviewDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  selectedApplication: Application | null
  interviewDetails: InterviewDetailsResponse | null
  setInterviewDetails: React.Dispatch<React.SetStateAction<InterviewDetailsResponse | null>>
  showScheduleForm: boolean
  setShowScheduleForm: React.Dispatch<React.SetStateAction<boolean>>
  scheduleFormData: ScheduleFormData
  setScheduleFormData: React.Dispatch<React.SetStateAction<ScheduleFormData>>
  validationErrors: ValidationErrors
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>
  emailInput: string
  setEmailInput: React.Dispatch<React.SetStateAction<string>>
  currentRoundForAction: any
  setCurrentRoundForAction: React.Dispatch<React.SetStateAction<any>>
  user: UserType | null
  isScheduling: boolean
  onScheduleSubmit: () => void
  onResultRound: (round: any, result: "Qualified" | "Rejected") => void
  onFetchJobLocation?: (jobId: string) => void
  showLoader: boolean
  setShowLoader?: (show: boolean) => void
}

export function InterviewDetailsModal({
  isOpen,
  onClose,
  selectedApplication,
  interviewDetails,
  setInterviewDetails,
  showScheduleForm,
  setShowScheduleForm,
  scheduleFormData,
  setScheduleFormData,
  validationErrors,
  setValidationErrors,
  emailInput,
  setEmailInput,
  currentRoundForAction,
  setCurrentRoundForAction,
  user,
  isScheduling,
  onScheduleSubmit,
  onResultRound,
  onFetchJobLocation,
  showLoader,
  setShowLoader
}: InterviewDetailsModalProps) {
  const [isRescheduleMode, setIsRescheduleMode] = React.useState(false)
  const [rescheduleReason, setRescheduleReason] = React.useState("")
  const [showFinalCheckbox, setShowFinalCheckbox] = React.useState(false)
  const [showOwnershipModal, setShowOwnershipModal] = React.useState(false)
  const [isOwnershipSubmitting, setIsOwnershipSubmitting] = React.useState(false)

  const ownershipConflict = getInterviewOwnershipConflict(selectedApplication, user?.id)

  const submitReschedule = async (options?: {
    transferOwnership?: boolean
    keepExistingOwner?: boolean
  }): Promise<boolean> => {
    if (!currentRoundForAction) return false

    const payload = {
      interviewType: scheduleFormData.interviewType,
      scheduledDate: scheduleFormData.scheduledDate,
      startTime: scheduleFormData.startTime,
      endTime: scheduleFormData.endTime,
      timeZone: scheduleFormData.timeZone,
      interviewers: scheduleFormData.interviewers,
      notes: scheduleFormData.notes,
      additionalParticipants: scheduleFormData.additionalParticipants,
      location: scheduleFormData.location,
      rescheduleReason: rescheduleReason,
      ...(options?.transferOwnership ? { transferOwnership: true } : {}),
      ...(options?.keepExistingOwner ? { keepExistingOwner: true } : {}),
    }

    try {
      setShowLoader?.(true)
      await rescheduleInterviewRound(
        selectedApplication?.interviewId || "",
        currentRoundForAction.id,
        payload
      )
      setShowScheduleForm(false)
      setIsRescheduleMode(false)
      setRescheduleReason("")

      await new Promise((resolve) => setTimeout(resolve, 1000))
      if (selectedApplication?.applicationId) {
        const updatedDetails = await getInterviewDetailsByApplicationId(selectedApplication.applicationId)
        setInterviewDetails(updatedDetails)
      }
      setShowLoader?.(false)
      toast.success("Interview has been rescheduled!")
      return true
    } catch (error: any) {
      console.error("Failed to reschedule interview:", error)
      setShowLoader?.(false)
      toast.error(error?.message || "Failed to reschedule interview")
      return false
    }
  }

  const handleScheduleSubmit = async () => {
    // Prevent scheduling if start time is not before end time
    const start = scheduleFormData.startTime;
    const end = scheduleFormData.endTime;
    if (!start || !end || start >= end) {
      setValidationErrors(prev => ({ ...prev, startTime: start >= end, endTime: start >= end }));
      return;
    }
    if (isRescheduleMode && currentRoundForAction) {
      if (!rescheduleReason.trim()) {
        setValidationErrors(prev => ({ ...prev, rescheduleReason: true }))
        return
      }
      setValidationErrors(prev => ({ ...prev, rescheduleReason: false }))

      if (ownershipConflict) {
        setShowOwnershipModal(true)
        return
      }

      void submitReschedule()
    } else {
      onScheduleSubmit()
    }
  }
  
  const renderInterviewDetails = () => {
    // Show loader when loading
    if (showLoader) {
      return (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-600">
          <svg
            className="animate-spin h-6 w-6 text-orange-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <span className="text-gray-700 font-medium">Loading interview details...</span>
        </div>
      )
    }

    if (!interviewDetails) {
      return null
    }

    const rounds = interviewDetails.data.interview.rounds || []
    const lastRound = getLastRound(rounds)
    const sortedRounds = [...rounds].reverse()

    return (
      <div className="space-y-6">
        <div className="space-y-6">
          {rounds.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-900 mb-2">No Rounds Scheduled</p>
              <p className="text-gray-600">Interview rounds have not been scheduled for this candidate yet.</p>
            </div>
          ) : (
            <div className="relative pb-6">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-600 via-orange-400 to-orange-200 rounded-full">
              </div>
                 
              <div className="space-y-6 relative z-10">
                {sortedRounds.map((round, index) => {
                  const isLastRound = round.id === lastRound?.id
                  const originalIndex = rounds.findIndex((r) => r.id === round.id)

                  return (
                    <div key={round.id} className="relative flex items-start">
                      <div className="absolute left-6 top-8 z-20">
                        <div
                          className="w-4 h-4 rounded-full border-4 border-white shadow-lg"
                          style={{
                            backgroundColor: "#e7550cff",
                            boxShadow: "0 0 0 3px rgba(231, 85, 12, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)",
                          }}
                        />
                      </div>

                      <div className="ml-16 flex-1 pb-2">
                        <div
                          className="bg-white border border-gray-250 rounded-lg p-4 sm:p-6 relative"
                          style={{ backgroundColor: "#e5ebf0ff", borderRadius: "20px", marginRight: "20px" }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 w-full">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 " style={{ fontStyle: "Roboto" }}>
                                R{originalIndex + 1} Interview
                              </h3>
                            </div>
                         
                          </div>

                          {round.scheduledDate && (
                            <>
                              <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-8 text-sm text-gray-600 mb-4 mt-3 sm:mb-6">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 flex-shrink-0" />
                                    <span className="font-medium">Date & Time:</span>
                                    <span className="break-words">
                                      {round.scheduledDate
                                        ? new Date(round.scheduledDate).toLocaleDateString("en-GB", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })
                                        : "Not Scheduled"}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 flex-shrink-0" />
                                    <span className="break-words">
                                      {round.startTime && round.endTime
                                        ? `${formatTimeTo12Hour(round.startTime)} - ${formatTimeTo12Hour(round.endTime)}`
                                        : "12:00 PM - 1:00 PM IST"}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium flex-shrink-0">Scheduled By:</span>
                                    <span className="text-gray-900">You</span>
                                  </div>
                                </div>
                              </div>

                              {/* Show interviewers feedback only after candidate is qualified or rejected */}
                              {round.feedback?.some(fb => fb.recommendation === "Qualified" || fb.recommendation === "Rejected") && (
                                <>
                                  <hr className="border-white mb-2 w-full" />
                                  <div className="mb-4 sm:mb-6">
                                    <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-3">
                                      Interviewers Feedback
                                    </h4>
                                    {round.feedback?.length > 0
                                      ? round.feedback.map((fb, idx) => (
                                          <div key={idx} className="">
                                            {fb.recommendation === "Rejected" && (
                                              <div className="mb-2">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                  Rejected by Company
                                                </span>
                                              </div>
                                            )}
                                            <p className="text-sm text-gray-600 ml-6 sm:ml-3 break-words">
                                              {fb.comments || "--"}
                                            </p>
                                          </div>
                                        ))
                                      : round.interviewers?.length > 0 && (
                                          <div className="mb-3">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-sm font-medium text-gray-900 break-words">
                                                {round.interviewers[0].username ||
                                                  round.interviewers[0].email ||
                                                  "Interviewer"}
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-600 ml-6 sm:ml-8">--</p>
                                          </div>
                                        )}
                                  </div>
                                </>
                              )}
                            </>
                          )}

                          {round.status === "COMPLETED" && (
                            <div
                              className={`flex items-center gap-2 text-sm font-medium ${
                                round.feedback?.some((fb: any) => fb.recommendation === "Rejected")
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              <span>
                                {(() => {
                                  const isRejected = round.feedback?.some((fb: any) => fb.recommendation === "Rejected")
                                  return isRejected ? (
                                    <>Rejected by Company</>
                                  ) : getNextRoundName(round, rounds) === "Final Selection" ? (
                                    <>Completed - {getNextRoundName(round, rounds)}</>
                                  ) : (
                                    <>Moved to {getNextRoundName(round, rounds)}</>
                                  )
                                })()}
                              </span>
                            </div>
                          )}

                          {isLastRound && !showScheduleForm && (
                            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 pt-4 border-t w-full justify-between items-center">
                              {canScheduleRound(round) && !round.scheduledDate && (
                                <Button
                                  onClick={async () => {
                                    setCurrentRoundForAction(round)
                                    const formData: ScheduleFormData = {
                                      interviewType: "Offline",
                                      scheduledDate: "",
                                      startTime: "",
                                      endTime: "",
                                      timeZone: "Asia/Kolkata",
                                      interviewers: [],
                                      notes: "",
                                      additionalParticipants: [],
                                      location: "",
                                    }
                                    setScheduleFormData(formData)
                                    setValidationErrors({ startTime: false, endTime: false, scheduledDate: false })
                                    if (selectedApplication?.job?.id && onFetchJobLocation) {
                                      await onFetchJobLocation(selectedApplication.job.id)
                                    }
                                    setShowScheduleForm(false)
                                    setIsRescheduleMode(false)
                                    setRescheduleReason("")
                                    setTimeout(() => {
                                      setShowScheduleForm(true)
                                    }, 50)
                                  }}
                                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg px-4 py-2 font-medium flex items-center"
                                >
                                  Schedule Interviews
                                </Button>
                              )}
                              {round.scheduledDate && (
                                <div className="flex w-full items-center">
                                  {/* Show Interview Rescheduled badge and Reschedule button if status is RESCHEDULED */}
                                  {round.status === "RESCHEDULED" && (
                                    <div className="flex items-center gap-2 mr-4">
                                      <span className="bg-purple-100 text-purple-700 px-4 py-1 rounded-full text-sm font-semibold">
                                        Interview Rescheduled
                                      </span>
                                      <Button
                                        onClick={() => {
                                          setCurrentRoundForAction(round)
                                          setScheduleFormData({
                                            interviewType: round.interviewType || "Online",
                                            scheduledDate: round.scheduledDate
                                              ? new Date(round.scheduledDate).toISOString().split("T")[0]
                                              : "",
                                            startTime: round.startTime || "",
                                            endTime: round.endTime || "",
                                            timeZone: round.timeZone || "Asia/Kolkata",
                                            interviewers: round.interviewers?.map((i: any) => i.id).filter(Boolean) || [],
                                            notes: round.notes || "",
                                            additionalParticipants: round.additionalParticipants || [],
                                            location: round.location || "",
                                          })
                                          setValidationErrors({ startTime: false, endTime: false, scheduledDate: false })
                                          setShowScheduleForm(false)
                                          setIsRescheduleMode(true)
                                          setRescheduleReason("")
                                          setTimeout(() => {
                                            setShowScheduleForm(true)
                                          }, 50)
                                        }}
                                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-2 font-medium ml-2"
                                      >
                                        Reschedule
                                      </Button>
                                    </div>
                                  )}

                                  {/* Show Qualify/Reject only if not RESCHEDULED, not completed, and not already processed */}
                                  {!(round.status === "RESCHEDULED" ||
                                      round.feedback?.some((fb: any) => fb.recommendation === "Qualified" || fb.recommendation === "Rejected") ||
                                      round.status === "COMPLETED" ||
                                      (isFinalRound(round, rounds) && round.feedback && round.feedback.length > 0)
                                    ) && (
                                    <div className="flex gap-2">
                                      <Button
                                        onClick={() => {
                                          setShowFinalCheckbox(true);
                                          onResultRound(round, "Qualified");
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 font-medium flex items-center"
                                      >
                                        <UserCheck className="h-4 w-4 mr-2" />
                                        Qualify
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          setShowFinalCheckbox(false);
                                          onResultRound(round, "Rejected");
                                        }}
                                        className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 py-2 font-medium flex items-center"
                                      >
                                        <UserX className="h-4 w-4 mr-2" />
                                        Reject
                                      </Button>

                                         <div className="flex items-center gap-2 mr-4">
                                      <Button
                                        onClick={() => {
                                          setCurrentRoundForAction(round)
                                          setScheduleFormData({
                                            interviewType: round.interviewType || "Online",
                                            scheduledDate: round.scheduledDate
                                              ? new Date(round.scheduledDate).toISOString().split("T")[0]
                                              : "",
                                            startTime: round.startTime || "",
                                            endTime: round.endTime || "",
                                            timeZone: round.timeZone || "Asia/Kolkata",
                                            interviewers: round.interviewers?.map((i: any) => i.id).filter(Boolean) || [],
                                            notes: round.notes || "",
                                            additionalParticipants: round.additionalParticipants || [],
                                            location: round.location || "",
                                          })
                                          setValidationErrors({ startTime: false, endTime: false, scheduledDate: false })
                                          setShowScheduleForm(false)
                                          setIsRescheduleMode(true)
                                          setRescheduleReason("")
                                          setTimeout(() => {
                                            setShowScheduleForm(true)
                                          }, 50)
                                        }}
                                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-2 font-medium ml-2"
                                      >
                                        Reschedule
                                      </Button>
                                    </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {isLastRound && showScheduleForm && (
                            <div className="mb-6">
                              {isRescheduleMode && (
                                <div className="mb-4">
                                  <Label className="text-sm font-semibold text-gray-800 mb-2 block">Reschedule Reason <span className="text-red-500">*</span></Label>
                                  <Input
                                    type="text"
                                    value={rescheduleReason}
                                    onChange={e => setRescheduleReason(e.target.value)}
                                    placeholder="Enter reason for rescheduling"
                                    className="max-w-[400px] w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 shadow-sm"
                                    required
                                  />
                                  {validationErrors.rescheduleReason && (
                                    <p className="text-red-500 text-sm mt-1">A reason for rescheduling is required.</p>
                                  )}
                                </div>
                              )}
                              <ScheduleForm
                                scheduleFormData={scheduleFormData}
                                setScheduleFormData={setScheduleFormData}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                emailInput={emailInput}
                                setEmailInput={setEmailInput}
                                currentRoundForAction={currentRoundForAction}
                                user={user}
                                isScheduling={isScheduling}
                                onSubmit={handleScheduleSubmit}
                                onCancel={() => {
                                  setShowScheduleForm(false)
                                  setIsRescheduleMode(false)
                                  setRescheduleReason("")
                                }}
                                selectedApplication={selectedApplication}
                                onFetchJobLocation={onFetchJobLocation}
                                isRescheduleMode={isRescheduleMode}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              
              <div className="h-6"></div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[90vw] max-w-[90vw] sm:max-w-[80vw] lg:max-w[70vw] xl:max-w-[60vw] 2xl:max-w-[55vw] max-h-[90vh] overflow-y-auto rounded-xl sm:rounded-2xl border-0 bg-white/95 backdrop-blur-sm p-4 sm:p-6 scrollbar-hide"
        style={{ borderRadius: "22px", scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <DialogHeader className="pb-6 border-b border-gray-100 ml-3">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            {showScheduleForm ? (isRescheduleMode ? "Reschedule Interview" : "Schedule Interview") : "Interview Details"}
          </DialogTitle>
          {selectedApplication && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-3 text-sm text-gray-600">
              <span className="flex items-center gap-2 font-medium">
                <User className="h-4 w-4" />
                {selectedApplication.candidate.name}
              </span>

              <span className="flex items-center gap-2 font-medium">
                <Building className="h-4 w-4" />
                {selectedApplication.job?.title}
              </span>
            </div>
          )}
        </DialogHeader>

        <div className="mt-4">{renderInterviewDetails()}</div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onClose()
              setShowScheduleForm(false)
              setIsRescheduleMode(false)
              setRescheduleReason("")
            }}
            className="rounded-xl border-gray-200 hover:bg-gray-50 px-6 py-3 font-medium transition-all duration-200"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <InterviewOwnershipModal
      isOpen={showOwnershipModal}
      onClose={() => {
        if (!isOwnershipSubmitting) setShowOwnershipModal(false)
      }}
      creatorName={ownershipConflict?.creatorName || "Unknown"}
      currentUserName={user?.username || "You"}
      isSubmitting={isOwnershipSubmitting}
      onConfirm={async () => {
        setIsOwnershipSubmitting(true)
        try {
          const success = await submitReschedule({ transferOwnership: true })
          if (success) setShowOwnershipModal(false)
        } finally {
          setIsOwnershipSubmitting(false)
        }
      }}
      onRescheduleKeepOwner={async () => {
        setIsOwnershipSubmitting(true)
        try {
          const success = await submitReschedule({ keepExistingOwner: true })
          if (success) setShowOwnershipModal(false)
        } finally {
          setIsOwnershipSubmitting(false)
        }
      }}
    />
    </>
  )
}